/**
 * ClaimHistory — list of past fee claim transactions.
 *
 * Shows amount, tx hash (linked to explorer), and timestamp for each claim.
 * Data comes from IClankerIndexerService via the creator fees API.
 *
 * Story 6.3 — Task 5
 */

import { formatUnits } from "viem";

import { useClaimHistory } from "../../hooks/api/use-claim-history";
import { formatRelativeTime } from "../../lib/format-number";
import { getBlockExplorerUrl, getNativeCurrencySymbol } from "../../lib/wagmi-config";

type ClaimHistoryProps = {
	tokenId: string;
};

function truncateHash(hash: string): string {
	return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
}

function safeFormatAmount(claimAmount: string, decimals: number): string {
	try {
		return formatUnits(BigInt(claimAmount), decimals);
	} catch {
		return "0";
	}
}

function ClaimHistorySkeleton() {
	return (
		<div className="space-y-2 animate-pulse">
			{Array.from({ length: 3 }).map((_, i) => (
				<div key={`claim-skeleton-${String(i)}`} className="flex items-center justify-between py-2">
					<div className="h-4 w-24 bg-pastel-light-gray rounded" />
					<div className="h-4 w-20 bg-pastel-light-gray rounded" />
					<div className="h-4 w-16 bg-pastel-light-gray rounded" />
				</div>
			))}
		</div>
	);
}

export function ClaimHistory({ tokenId }: ClaimHistoryProps) {
	const { history, isLoading } = useClaimHistory(tokenId);

	const explorerUrl = getBlockExplorerUrl();
	const symbol = getNativeCurrencySymbol();

	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5">
			<h3 className="text-sm font-semibold text-pastel-black mb-3">Claim History</h3>

			{isLoading ? (
				<ClaimHistorySkeleton />
			) : history.length === 0 ? (
				<p className="text-sm text-pastel-gray py-4 text-center">No claims yet</p>
			) : (
				<div className="space-y-0">
					{/* Header row */}
					<div className="grid grid-cols-3 gap-2 text-xs font-medium text-pastel-gray uppercase tracking-wide pb-2 border-b border-pastel-border">
						<span>Amount</span>
						<span>Tx Hash</span>
						<span className="text-right">Time</span>
					</div>

					{/* Claim entries */}
					{history.map((entry) => (
						<div
							key={entry.txHash}
							className="grid grid-cols-3 gap-2 py-2 border-b border-pastel-border/50 last:border-0"
						>
							<span className="text-sm font-medium text-pastel-black">
								{safeFormatAmount(entry.claimAmount, 18)} {symbol}
							</span>
							<a
								href={`${explorerUrl}/tx/${entry.txHash}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-sm text-pastel-vibrant-blue hover:underline"
							>
								{truncateHash(entry.txHash)}
							</a>
							<span className="text-sm text-pastel-gray text-right" title={entry.timestamp}>
								{formatRelativeTime(entry.timestamp)}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
